/** Filter pills for the Visitors Desk. A filter is one dimension value set by
 *  clicking a row ("Channel: Social"). It lives in the URL as `f.<key>`. */
import { deskDates, type DeskQuery, type DeskTab } from './deskPeriod'

export const FILTER_KEYS = ['channel', 'channel_detail', 'referrer_host', 'landing', 'article', 'issue', 'country', 'device', 'region'] as const
export type FilterKey = typeof FILTER_KEYS[number]
export interface DeskFilter { key: FilterKey; value: string }

export function filtersFromSearch(sp: URLSearchParams): DeskFilter[] {
  const out: DeskFilter[] = []
  for (const key of FILTER_KEYS) {
    const value = sp.get(`f.${key}`)
    if (value) out.push({ key, value })
  }
  return out
}

export function filtersToSearch(fs: DeskFilter[]): Record<string, string> {
  const out: Record<string, string> = {}
  for (const f of fs) if (f.value) out[`f.${f.key}`] = f.value
  return out
}

/** Which filters each tab's screens actually re-query on. Sources applies only
 *  the referrer host (its drill); channel pills there highlight rows, they do
 *  not filter. Articles and Issues open a row's page instead of a pill. Search
 *  has no first-party dimensions, so like Today and Trends it applies none. */
export const TAB_FILTERS: Record<DeskTab, FilterKey[]> = {
  today: [],
  sources: ['referrer_host'],
  search: [],
  flows: ['channel', 'landing'],
  articles: [],
  issues: [],
  audience: [],
  trends: [],
  revenue: [],
}

/** Filters set on the page that this tab cannot apply: they get the grey "Not filtered by …" tag. */
export function unappliedFilters(tab: DeskTab, fs: DeskFilter[]): DeskFilter[] {
  const ok = TAB_FILTERS[tab]
  return fs.filter((f) => !ok.includes(f.key))
}

const LABELS: Record<FilterKey, string> = {
  channel: 'Channel', channel_detail: 'Source', referrer_host: 'Referrer', landing: 'Landing page',
  article: 'Article', issue: 'Issue', country: 'Country', device: 'Device', region: 'Region',
}

export function filterLabel(f: DeskFilter): string {
  return `${LABELS[f.key]}: ${f.value}`
}

/** What the existing sessions list can filter by. */
const SESSION_KEYS: FilterKey[] = ['channel', 'landing', 'country', 'device', 'referrer_host', 'channel_detail', 'region']

/** The pill's "See the visits →" link: the existing sessions list with the
 *  filters it understands and the period as an old-style custom range. Empty
 *  for `all` (that list is per title). Pass `today` (Eastern, ISO) to resolve the period. */
export function sessionsLink(pub: string, fs: DeskFilter[], q: DeskQuery, today: string): string {
  if (pub === 'all') return ''
  const { from, to } = deskDates(q, today)
  const parts = ['range=custom', `from=${from}`, `to=${to}`]
  for (const f of fs) if (SESSION_KEYS.includes(f.key)) parts.push(`${f.key}=${encodeURIComponent(f.value)}`)
  return `/visitors/${encodeURIComponent(pub)}/sessions?${parts.join('&')}`
}
